import { Component, computed, inject, input, InputSignal, Signal } from '@angular/core';
import { Icon } from '../icon/icon';
import { TrainingSectionType } from '../../models/training-section.model';
import { WebSocketService } from '../../services/websocket-service';
import { getTrainingSectionColor } from '../../helpers/training-section-color.helpers';
import { DistancePipe } from '../../pipes/distance-pipe';
import { UnitType } from '../../models/unit.model';
import { ApiService } from '../../services/api-service';
import { TrainingModeType } from '../../models/training-mode.model';
import { ChronoPipe } from '../../pipes/chrono-pipe';
import { CoachingIntervalType } from '../../models/coaching-interval.model';
import { ColorService } from '../../services/color-service';
import { TranslateModule } from '@ngx-translate/core';

type ChartSegmentInfoVariant = 'scenic' | 'coaching';

@Component({
  selector: 'app-chart-next-segment-info',
  imports: [Icon, DistancePipe, ChronoPipe, TranslateModule],
  template: `
    <div class="next-instruction" [class.coaching]="variant() === 'coaching'">
      @if (variant() === 'coaching') {
        <p class="chrono">{{ nextCoachingTime() | chrono: 'short' }}</p>
        <app-icon icon="instruction_next" [size]="3" />
        @if (nextInterval()) {
          <p class="metric">{{ nextCadence() }} {{ cadenceUnit() | translate }}</p>
          <p class="value percent" [style.background]="nextIntervalColor()">
            {{ nextIntervalPercent() }}%
          </p>
        } @else {
          <img class="end-marker" src="images/map_marker_end.svg" alt="end marker" />
        }
      } @else {
        <app-icon icon="mountain-next" [size]="3" />
        @if (trainingMode() === 'variableSpeed') {
          <p class="distance" [attr.data-unit]="nextDistance()! | distance: unit : 'unit'">
            {{ nextDistance()! | distance: unit : 'value' }}
          </p>
        } @else {
          <p class="chrono">{{ nextTime() || 0 | chrono }}</p>
        }
        @if (nextTrainingSection()) {
          <p
            class="value slope-value"
            [style.background]="nextInstructionSlopeColor()"
            [class.positive]="nextInstructionSlope()! > 0"
          >
            {{ nextInstructionSlope() }}
          </p>
        } @else {
          <img class="end-marker" src="images/map_marker_end.svg" alt="end marker" />
        }
      }
    </div>
  `,
  styleUrl: './chart-next-segment-info.scss',
})
export class ChartNextSegmentInfo {
  private readonly webSocketService: WebSocketService = inject(WebSocketService);
  private readonly apiService: ApiService = inject(ApiService);
  private readonly colorService: ColorService = inject(ColorService);

  readonly variant: InputSignal<ChartSegmentInfoVariant> = input<ChartSegmentInfoVariant>('scenic');

  private readonly trainingSections: Signal<TrainingSectionType[]> = computed(
    () => this.apiService.scenicTrainingSections() ?? [],
  );
  protected readonly unit: UnitType = (localStorage.getItem('unit') as UnitType) || 'metric';

  protected readonly trainingMode: Signal<TrainingModeType> = computed(
    () => this.webSocketService.contentInformation()?.trainingMode || 'originalSpeed',
  );

  protected nextTrainingSection: Signal<TrainingSectionType | undefined> = computed(() => {
    if (this.trainingMode() === 'originalSpeed') {
      const currentTime: number = this.webSocketService.broadcastData()?.data.time || 0;
      const passedSection: number = this.trainingSections().filter(
        (section: TrainingSectionType) => section.position < currentTime,
      ).length;
      return this.trainingSections()[passedSection] ?? undefined;
    }

    const currentDistance: number = this.webSocketService.broadcastData()?.data.distance || 0;
    const passedSection: number = this.trainingSections().filter(
      (trainingSection: TrainingSectionType) => trainingSection.distance <= currentDistance,
    ).length;
    return this.trainingSections()[passedSection] ?? undefined;
  });

  protected readonly nextInstructionSlope: Signal<number | undefined> = computed<
    number | undefined
  >(() => {
    if (this.nextTrainingSection()) {
      return Math.round(this.nextTrainingSection()!.slope * 10) / 10;
    }
    return undefined;
  });

  protected readonly nextTime: Signal<number | undefined> = computed<number | undefined>(() => {
    if (!this.nextTrainingSection()) return undefined;
    return this.nextTrainingSection()!.position - this.currentTime();
  });

  protected readonly nextDistance: Signal<number | undefined> = computed<number | undefined>(() => {
    if (!this.nextTrainingSection()) return undefined;
    return parseInt((this.nextTrainingSection()!.distance - this.currentDistance()).toFixed());
  });

  protected readonly nextInstructionSlopeColor: Signal<string | undefined> = computed<
    string | undefined
  >(() => {
    const slope: number | undefined = this.nextInstructionSlope();
    if (slope === undefined) return undefined;
    return getTrainingSectionColor(slope);
  });

  protected readonly currentDistance: Signal<number> = computed<number>(
    () => this.webSocketService.broadcastData()?.data.distance || 0,
  );

  protected readonly currentTime: Signal<number> = computed<number>(
    () => this.webSocketService.broadcastData()?.data.time || 0,
  );

  protected readonly nextInterval: Signal<CoachingIntervalType | undefined> = computed(() => {
    const intervals: CoachingIntervalType[] = this.apiService.coachingIntervals() ?? [];
    const currentTime: number = this.currentTime();
    return (
      intervals.find((interval: CoachingIntervalType) => interval.position > currentTime) ||
      undefined
    );
  });

  protected readonly nextCoachingTime: Signal<number> = computed(() => {
    const nextIntervalTime: number = this.nextInterval()?.position ?? this.currentTime();
    return Math.max(nextIntervalTime - this.currentTime(), 0);
  });

  protected readonly nextCadence: Signal<number> = computed(
    () => this.nextInterval()?.optional_value ?? 0,
  );

  protected readonly nextIntervalPercent: Signal<number> = computed(
    () => this.nextInterval()?.value ?? 0,
  );

  protected readonly nextIntervalColor: Signal<string> = computed(() =>
    this.colorService.getColor(this.nextIntervalPercent()),
  );

  protected readonly cadenceUnit: Signal<string> = computed(() => {
    const sport: string = this.apiService.scenicInformation()?.sport || 'cycling';
    return sport === 'running' ? 'km/h' : `common.metric.cadence.${sport}`;
  });
}
